import type { LeadBucket } from '@utazzo/shared';

/**
 * Stage color resolution (ADR-0032 §18) - shared across pipeline tiles,
 * aside chips, detail header badges, and grid rows so the same stage always
 * reads the same color regardless of where it renders.
 *
 * Three inputs, in priority order:
 *   1. `color` (hex string) from the QueryStatus / QuerySubStatus master.
 *      Set by ops via the Masters CMS; the DB column lives on
 *      `query_statuses.color` and `query_sub_statuses.color`.
 *   2. `bucket` - when the QueryStatus row has its `bucket` set
 *      (OPEN / WON / LOST), that's the next-best truth.
 *   3. `name`  - for legacy-migrated stages where both color and bucket are
 *      NULL, we infer a tone from the human label.
 *
 * The result is shaped as:
 *   - `dotStyle()` → an inline `{ backgroundColor }` style object, used by
 *     the indicator dots. Inline styles are required because the operator-
 *     authored color could be any hex; pre-baking Tailwind classes for an
 *     unbounded set isn't viable.
 *   - `badgeTone()` → a discrete `UzBadge` tone enum, used by stage pills.
 *     UzBadge intentionally limits its palette to the design tokens, so
 *     custom hex colors collapse onto the nearest semantic tone (OPEN
 *     `default`, WON `success`, LOST `destructive`). The dot underneath
 *     carries the precise color; the pill carries the intent.
 *
 * Performance note: the helper is a pure function. No memoization needed -
 * call sites pass concrete primitives, and React skips re-renders when the
 * resolved style object is stored in a memoized parent (callers already
 * memoize tiles / rows).
 */

export type StageTone = 'open' | 'won' | 'lost' | 'cool' | 'neutral';

const TONE_BY_BUCKET: Record<LeadBucket, StageTone> = {
  OPEN: 'open',
  WON: 'won',
  LOST: 'lost',
};

/** Same Tailwind 500-shade tokens used by the chip backgrounds. */
const TONE_HEX: Record<StageTone, string> = {
  open: '#3b82f6',
  won: '#10b981',
  lost: '#ef4444',
  cool: '#f59e0b',
  neutral: '#a1a1aa',
};

/**
 * Terminal labels (Won / Converted / Booked / Lost / Dropped / Cancelled).
 * Returned tone takes precedence over `bucket` in the public resolver
 * because legacy data frequently has the bucket column either NULL or
 * mis-denormed (e.g. a "Won" status whose row still carries bucket=OPEN
 * from the ETL backfill). The name is the authoritative semantic signal -
 * a status called "Won" should always render as won regardless of which
 * bucket the row claims it sits in.
 */
const inferTerminalToneFromName = (name: string): StageTone | null => {
  const lc = name.toLowerCase();
  if (lc.includes('won') || lc.includes('converted') || lc.includes('booked')) return 'won';
  if (lc.includes('lost') || lc.includes('dropped') || lc.includes('cancel')) return 'lost';
  return null;
};

/**
 * Cool-side name match (Not Qualified / Not Contacted / Dormant / Cold /
 * New). Checked BEFORE active-pipeline inference so "Not Qualified"
 * doesn't accidentally match `'qualified'` (substring inside the longer
 * label) and get tagged as an active stage. Returns `null` when the name
 * carries no cool signal.
 */
const inferCoolToneFromName = (name: string): StageTone | null => {
  const lc = name.toLowerCase();
  if (
    lc.includes('not qualified') ||
    lc.includes('not contacted') ||
    lc.includes('dormant') ||
    lc.includes('cold') ||
    lc.includes('new')
  ) {
    return 'cool';
  }
  return null;
};

/**
 * Active-pipeline name match (Active / Hot / Warm / Proposal / Contacted /
 * Qualified / Quoted / Negotiation / Engaged). Same role as
 * `inferTerminalToneFromName`: when the name unambiguously denotes an
 * active-pipeline stage, that signal beats `bucket` because legacy ETL'd
 * rows often have the bucket mis-denormed (a "Hot" status with bucket=WON
 * is a backfill bug, not a deliberate ops choice). Returns `null` when
 * the name carries no active-pipeline signal.
 *
 * Must be checked AFTER `inferCoolToneFromName` because `'qualified'`
 * appears as a substring in `'not qualified'`.
 */
const inferActivePipelineToneFromName = (name: string): StageTone | null => {
  const lc = name.toLowerCase();
  if (
    lc.includes('active') ||
    lc.includes('hot') ||
    lc.includes('warm') ||
    lc.includes('engaged') ||
    lc.includes('proposal') ||
    lc.includes('contacted') ||
    lc.includes('qualified') ||
    lc.includes('quoted') ||
    lc.includes('negotiation')
  ) {
    return 'open';
  }
  return null;
};

/**
 * Resolve a semantic tone from bucket + name (without considering the custom
 * color). Used by `badgeTone()` because `UzBadge` only accepts the discrete
 * tone enum, not arbitrary hex.
 *
 * Priority order:
 *   1. Terminal name match (won / converted / booked / lost / dropped /
 *      cancelled) - beats bucket because the name is the authoritative
 *      signal for terminal outcomes.
 *   2. Cool name match (not qualified / not contacted / dormant / cold /
 *      new) - checked BEFORE active-pipeline so "Not Qualified" doesn't
 *      accidentally match `'qualified'` (substring) and turn blue.
 *   3. Active-pipeline name match (active / hot / warm / proposal …) -
 *      beats bucket too, because legacy data often has the bucket field
 *      mis-set (e.g. "Hot" with bucket=WON should still render as open).
 *   4. Bucket - OPEN / WON / LOST → matching tone.
 *   5. Neutral.
 *
 * Note: the public `resolveStageHex` wrapper checks the DB color FIRST,
 * so an operator-set color via the Masters CMS always wins over every
 * step above - preserving the CMS as the source of truth.
 */
export const resolveStageTone = (
  bucket: LeadBucket | null | undefined,
  name: string,
): StageTone => {
  const terminal = inferTerminalToneFromName(name);
  if (terminal) return terminal;
  // Cool BEFORE active-pipeline so "Not Qualified" doesn't substring-match
  // `'qualified'` and fall into the open tone.
  const cool = inferCoolToneFromName(name);
  if (cool) return cool;
  const activePipeline = inferActivePipelineToneFromName(name);
  if (activePipeline) return activePipeline;
  if (bucket && TONE_BY_BUCKET[bucket]) return TONE_BY_BUCKET[bucket];
  return 'neutral';
};

/**
 * Resolve the indicator hex. Custom DB color wins; otherwise fall through
 * to the tone-mapped default.
 */
export const resolveStageHex = (
  color: string | null | undefined,
  bucket: LeadBucket | null | undefined,
  name: string,
): string => {
  if (color) return color;
  return TONE_HEX[resolveStageTone(bucket, name)];
};

/**
 * Inline style for the indicator dot - always pair with a fixed sizing
 * class like `h-2 w-2 rounded-full inline-block`.
 */
export const stageDotStyle = (
  color: string | null | undefined,
  bucket: LeadBucket | null | undefined,
  name: string,
): React.CSSProperties => ({
  backgroundColor: resolveStageHex(color, bucket, name),
});

/** `UzBadge` tone for the stage pill (still tone-based, not hex). */
export const stageBadgeTone = (
  tone: StageTone,
): 'default' | 'success' | 'destructive' | 'warning' | 'secondary' => {
  switch (tone) {
    case 'open':
      return 'default';
    case 'won':
      return 'success';
    case 'lost':
      return 'destructive';
    case 'cool':
      return 'warning';
    case 'neutral':
    default:
      return 'secondary';
  }
};
